import React, { useState, useMemo } from "react";
import { UpdateData, getCurrency, API_BASE, API_HEADERS } from "../service/APIService";
import { ThemedDatePicker } from "../components1/ThemedCalendar";
import useSheetDismiss from "../utils/useSheetDismiss";

const PREDEFINED_CATEGORIES = [
  "Groceries",
  "Food",
  "Shopping",
  "Transport",
  "Subscription",
  "Rent",
  "Fuel",
  "Gift",
  "Travel",
  "Saving",
  "Healthcare",
  "Entertainment",
  "Other"
];

const FREQUENT_LIMIT = 4;

const getCategoryIcon = (category = "") => {
  const cat = category.toLowerCase();
  if (cat.includes("food") || cat.includes("rest") || cat.includes("drink") || cat.includes("cafe")) return "fi fi-rr-utensils";
  if (cat.includes("grocer") || cat.includes("supermarket")) return "fi fi-rr-shopping-bag";
  if (cat.includes("shop") || cat.includes("cloth") || cat.includes("shoe")) return "fi fi-rr-shop";
  if (cat.includes("travel") || cat.includes("fuel") || cat.includes("uber") || cat.includes("car") || cat.includes("transport")) return "fi fi-rr-car";
  if (cat.includes("rent") || cat.includes("home") || cat.includes("house")) return "fi fi-rr-home";
  if (cat.includes("sub") || cat.includes("stream") || cat.includes("tech") || cat.includes("apple") || cat.includes("phone")) return "fi fi-rr-laptop";
  if (cat.includes("health") || cat.includes("med") || cat.includes("doctor")) return "fi fi-rr-heart";
  if (cat.includes("gift") || cat.includes("party")) return "fi fi-rr-gift";
  if (cat.includes("save") || cat.includes("bank") || cat.includes("invest")) return "fi fi-rr-piggy-bank";
  return "fi fi-rr-receipt";
};

const History = ({ expenses = [], country, isGuest, onChanged, categories = [] }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [visibleExpenses, setVisibleExpenses] = useState(15);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedExpense, setSelectedExpense] = useState(null); // Detail sheet
  const { closing: detailClosing, requestClose: closeDetail } = useSheetDismiss(() => setSelectedExpense(null));
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showAllCategories, setShowAllCategories] = useState(false);

  const currentCategory = editForm?.category || "";

  // Compute all available categories from predefined, custom fetched, and existing expenses
  const allCategories = useMemo(() => {
    const set = new Set([...PREDEFINED_CATEGORIES, ...categories]);
    if (Array.isArray(expenses)) {
      expenses.forEach((e) => {
        if (e && e.category) set.add(e.category);
      });
    }
    if (currentCategory) set.add(currentCategory);
    return Array.from(set);
  }, [categories, expenses, currentCategory]);

  const visibleCategories = useMemo(() => {
    if (showAllCategories) return allCategories;
    const top = allCategories.slice(0, FREQUENT_LIMIT);
    if (currentCategory && !top.some((c) => c.toLowerCase() === currentCategory.toLowerCase())) {
      top.push(currentCategory);
    }
    return top;
  }, [allCategories, showAllCategories, currentCategory]);

  const handleCategorySelect = (cat) => {
    setEditForm((prev) => ({ ...prev, category: cat }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const options = { day: "2-digit", month: "short" };
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("en-US", options);
  };

  const formatFullDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" });
  };

  const getRelativeDateGroup = (dateString) => {
    if (!dateString) return "Other";
    const today = new Date();
    const [year, month, day] = dateString.split("-").map(Number);
    const expDate = new Date(year, month - 1, day);

    const isToday =
      today.getFullYear() === expDate.getFullYear() &&
      today.getMonth() === expDate.getMonth() &&
      today.getDate() === expDate.getDate();

    if (isToday) return "Today";

    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const isYesterday =
      yesterday.getFullYear() === expDate.getFullYear() &&
      yesterday.getMonth() === expDate.getMonth() &&
      yesterday.getDate() === expDate.getDate();

    if (isYesterday) return "Yesterday";

    return expDate.toLocaleDateString("en-US", { day: "2-digit", month: "long" });
  };

  const deleteExpense = async (id) => {
    const isConfirmed = window.confirm("Are you sure you want to delete this expense?");
    if (!isConfirmed) return;
    setLoading(true);
    try {
      if (!isGuest) {
        const res = await fetch(`${API_BASE}/removeitem/${id}`, {
          method: "DELETE",
          headers: API_HEADERS,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      } else {
        let guestExpenses = JSON.parse(localStorage.getItem("guestExpenses")) || [];
        guestExpenses = guestExpenses.filter((expense) => expense.id !== id);
        localStorage.setItem("guestExpenses", JSON.stringify(guestExpenses));

        let guestShared = JSON.parse(localStorage.getItem("guestSharedExpenses")) || [];
        guestShared = guestShared.filter((expense) => expense.id !== id);
        localStorage.setItem("guestSharedExpenses", JSON.stringify(guestShared));
      }
      setSelectedExpense(null);
      setIsEditing(false);
      setError("");
      await onChanged();
    } catch (err) {
      setError("Failed to delete expense.");
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (expense) => {
    setSelectedExpense(expense);
    setEditForm({ ...expense });
    setIsEditing(false);
    setShowAllCategories(false);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm({ ...editForm, [name]: value });
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editForm.amount || !editForm.description || !editForm.category) {
      alert("All fields including category are required.");
      return;
    }

    setLoading(true);
    try {
      const updatedExpense = {
        ...editForm,
        amount: parseFloat(editForm.amount),
        category: editForm.category.trim(),
      };

      if (!isGuest) {
        await UpdateData(updatedExpense);
      } else {
        let guestExpenses = JSON.parse(localStorage.getItem("guestExpenses")) || [];
        guestExpenses = guestExpenses.map((exp) => (exp.id === editForm.id ? updatedExpense : exp));
        localStorage.setItem("guestExpenses", JSON.stringify(guestExpenses));

        let guestShared = JSON.parse(localStorage.getItem("guestSharedExpenses")) || [];
        guestShared = guestShared.map((exp) => (exp.id === editForm.id ? updatedExpense : exp));
        localStorage.setItem("guestSharedExpenses", JSON.stringify(guestShared));
      }

      setSelectedExpense(null);
      setIsEditing(false);
      setError("");
      await onChanged();
    } catch (err) {
      setError("Failed to update expense.");
    } finally {
      setLoading(false);
    }
  };

  const filteredExpenses = [...expenses]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .filter((expense) => {
      const q = searchTerm.toLowerCase();
      return (
        expense.description?.toLowerCase().includes(q) ||
        expense.category?.toLowerCase().includes(q)
      );
    });

  // Group filtered expenses by relative date
  const groupedByDate = filteredExpenses.slice(0, visibleExpenses).reduce((groups, expense) => {
    const groupKey = getRelativeDateGroup(expense.date);
    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(expense);
    return groups;
  }, {});

  return (
    <div className="history-section mb-4">
      {/* Header & Search */}
      <div className="d-flex justify-content-between align-items-center mb-2 px-1">
        <h6 className="mb-0 fw-bold" style={{ fontSize: "16px", color: "var(--primary-dark)" }}>
          Transactions
        </h6>
        <span className="badge" style={{ backgroundColor: "var(--accent-mauve-soft)", color: "var(--primary-deep)", fontSize: "11px", borderRadius: "12px", padding: "5px 10px" }}>
          {filteredExpenses.length} items
        </span>
      </div>

      {expenses.length > 0 && (
        <div className="position-relative mb-3 mt-2">
          <input
            type="text"
            placeholder="Search transactions..."
            className="modern-input"
            style={{ paddingLeft: "38px", height: "44px", borderRadius: "22px" }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <i
            className="fi fi-rr-search position-absolute"
            style={{ left: "14px", top: "13px", color: "var(--text-muted)", fontSize: "15px" }}
          ></i>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="position-absolute border-0 bg-transparent"
              style={{ right: "12px", top: "11px", color: "var(--text-muted)", cursor: "pointer" }}
            >
              <i className="fi fi-rr-cross-small" style={{ fontSize: "18px" }}></i>
            </button>
          )}
        </div>
      )}

      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {loading && !selectedExpense ? (
        <div className="text-center py-4 text-muted">
          <i className="fas fa-spinner fa-spin me-2" style={{ color: "var(--accent-coral)" }}></i>
          Loading transactions...
        </div>
      ) : expenses.length === 0 ? (
        <div className="text-center py-5" style={{ background: "var(--surface-card)", borderRadius: "var(--radius-md)", border: "1px dashed var(--border-subtle)" }}>
          <i className="fi fi-rr-receipt mb-2" style={{ fontSize: "36px", color: "var(--accent-mauve)" }}></i>
          <p className="mb-0 text-muted small">No expenses recorded yet.</p>
          <small className="text-muted">Tap the + button to add your first expense.</small>
        </div>
      ) : Object.keys(groupedByDate).length === 0 ? (
        <div className="text-center py-4 text-muted small">
          No matches found for "{searchTerm}"
        </div>
      ) : (
        <>
          {Object.entries(groupedByDate).map(([dateGroup, items]) => (
            <div key={dateGroup} className="mb-3">
              <div className="transaction-group-title">{dateGroup}</div>
              {items.map((expense) => {
                const iconClass = getCategoryIcon(expense.category);
                return (
                  <div
                    key={expense.id}
                    className="transaction-card"
                    onClick={() => handleCardClick(expense)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="transaction-left">
                      <div className="category-icon-circle">
                        <i className={iconClass}></i>
                      </div>
                      <div className="transaction-details">
                        <h5>{expense.description}</h5>
                        <span>{expense.category}</span>
                      </div>
                    </div>

                    <div className="transaction-right">
                      <div className="transaction-amount expense">
                        - {getCurrency(country)}
                        {Number(expense.amount).toFixed(2)}
                      </div>
                      <div className="transaction-subtext">{formatDate(expense.date)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {visibleExpenses < filteredExpenses.length && (
            <div className="text-center mt-3 mb-4">
              <button
                className="btn btn-sm"
                style={{
                  background: "var(--surface-card)",
                  color: "var(--primary-deep)",
                  border: "1px solid var(--border-subtle)",
                  borderRadius: "20px",
                  padding: "8px 24px",
                  fontWeight: 600,
                  fontSize: "12px",
                  boxShadow: "var(--shadow-sm)"
                }}
                onClick={() => setVisibleExpenses((prev) => prev + 15)}
              >
                Load More ({filteredExpenses.length - visibleExpenses} remaining)
              </button>
            </div>
          )}
        </>
      )}

      {/* Modern Slide-Up Bottom Sheet for Viewing / Editing / Deleting Expense */}
      {selectedExpense && (
        <div className="bottom-sheet-overlay" onClick={closeDetail}>
          <div className={`bottom-sheet-content${detailClosing ? " sheet-closing" : ""}`} onClick={(e) => e.stopPropagation()}>
            <div className="bottom-sheet-handle" onClick={closeDetail}></div>

            {!isEditing ? (
              /* View / Detail Mode matching Image 1 detail screen */
              <div>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <span
                      className="badge mb-2"
                      style={{
                        backgroundColor: "var(--accent-coral-soft)",
                        color: "var(--accent-coral)",
                        fontWeight: 600,
                        fontSize: "12px",
                        padding: "5px 12px",
                        borderRadius: "16px"
                      }}
                    >
                      {selectedExpense.category}
                    </span>
                    <h3 className="fw-bold mb-0" style={{ color: "var(--primary-dark)", fontSize: "28px" }}>
                      - {getCurrency(country)}
                      {Number(selectedExpense.amount).toFixed(2)}
                    </h3>
                  </div>

                  <div className="d-flex gap-2">
                    <button
                      className="header-icon-btn"
                      onClick={() => setIsEditing(true)}
                      title="Edit Expense"
                    >
                      <i className="fi fi-rr-edit" style={{ color: "var(--primary-deep)" }}></i>
                    </button>
                    <button
                      className="header-icon-btn"
                      onClick={() => deleteExpense(selectedExpense.id)}
                      title="Delete Expense"
                    >
                      <i className="fi fi-rr-trash" style={{ color: "var(--danger-color)" }}></i>
                    </button>
                  </div>
                </div>

                <div
                  className="p-3 mb-4"
                  style={{
                    background: "var(--bg-color)",
                    borderRadius: "var(--radius-md)",
                    border: "1px solid var(--border-subtle)"
                  }}
                >
                  <div className="mb-2">
                    <small className="text-muted d-block" style={{ fontSize: "11px", fontWeight: 600 }}>
                      DESCRIPTION
                    </small>
                    <span className="fw-medium" style={{ color: "var(--primary-dark)" }}>
                      {selectedExpense.description}
                    </span>
                  </div>

                  <div>
                    <small className="text-muted d-block" style={{ fontSize: "11px", fontWeight: 600 }}>
                      DATE
                    </small>
                    <span className="fw-medium" style={{ color: "var(--primary-dark)" }}>
                      {formatFullDate(selectedExpense.date)}
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn-primary-modern"
                  onClick={closeDetail}
                >
                  Done
                </button>
              </div>
            ) : (
              /* Edit Mode */
              <form onSubmit={handleEditSave}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="fw-bold mb-0" style={{ color: "var(--primary-dark)" }}>
                    Edit Expense
                  </h5>
                  <button
                    type="button"
                    className="border-0 bg-transparent text-muted"
                    onClick={() => setIsEditing(false)}
                  >
                    <i className="fi fi-rr-cross" style={{ fontSize: "16px" }}></i>
                  </button>
                </div>

                <div className="modern-input-group">
                  <label>Amount{getCurrency(country) ? ` (${getCurrency(country)})` : ""}</label>
                  <input
                    type="number"
                    name="amount"
                    step="any"
                    min="0"
                    inputMode="decimal"
                    className="modern-input modern-input-amount"
                    value={editForm.amount}
                    onChange={handleEditChange}
                    required
                  />
                </div>

                {/* Category Selection */}
                <div className="modern-input-group mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="mb-0" style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-muted)" }}>
                      Category {!showAllCategories && <span style={{ fontWeight: 400, opacity: 0.8 }}>(Frequently Used)</span>}
                    </label>
                    {allCategories.length > FREQUENT_LIMIT && (
                      <button
                        type="button"
                        className="border-0 bg-transparent p-0"
                        style={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "var(--accent-coral)",
                          cursor: "pointer",
                        }}
                        onClick={() => setShowAllCategories(!showAllCategories)}
                      >
                        {showAllCategories ? "Show Less ▴" : `Show All (${allCategories.length}) ▾`}
                      </button>
                    )}
                  </div>

                  <div className="category-2col-grid">
                    {visibleCategories.map((cat, idx) => {
                      const isSelected = (editForm.category || "").toLowerCase() === cat.toLowerCase();
                      return (
                        <button
                          key={idx}
                          type="button"
                          className={`category-2col-btn ${isSelected ? "active" : ""}`}
                          onClick={() => handleCategorySelect(cat)}
                        >
                          <i className={getCategoryIcon(cat)}></i>
                          <span>{cat}</span>
                        </button>
                      );
                    })}

                    {!showAllCategories && allCategories.length > FREQUENT_LIMIT && (
                      <button
                        type="button"
                        className="category-2col-more-btn"
                        onClick={() => setShowAllCategories(true)}
                        title="Show all categories"
                      >
                        <span>+ More ({allCategories.length - FREQUENT_LIMIT})</span>
                        <i className="fi fi-rr-angle-small-down"></i>
                      </button>
                    )}

                    {showAllCategories && allCategories.length > FREQUENT_LIMIT && (
                      <button
                        type="button"
                        className="category-2col-more-btn"
                        onClick={() => setShowAllCategories(false)}
                        title="Show fewer categories"
                      >
                        <span>Show Less</span>
                        <i className="fi fi-rr-angle-small-up"></i>
                      </button>
                    )}
                  </div>
                </div>

                <div className="modern-input-group">
                  <label>Description</label>
                  <input
                    type="text"
                    name="description"
                    className="modern-input"
                    value={editForm.description}
                    onChange={handleEditChange}
                    required
                  />
                </div>

                <div className="modern-input-group">
                  <ThemedDatePicker
                    value={editForm.date}
                    onChange={handleEditChange}
                    name="date"
                    label="Date"
                    required
                  />
                </div>

                <div className="d-flex gap-2 mt-4">
                  <button
                    type="button"
                    className="btn btn-light flex-grow-1"
                    style={{ borderRadius: "var(--radius-md)", padding: "12px", fontWeight: 600 }}
                    onClick={() => setIsEditing(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-coral-modern flex-grow-1"
                    disabled={loading}
                  >
                    {loading ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default History;
